// 패딩 있는 모든 노드의 패딩 영역(노드 박스 ↔ 콘텐츠 사이)을 반투명 색으로 표시 — admin 편집 캔버스 전용.
// BindingBadgeOverlay와 동일한 캔버스 상대좌표 환산(getBoundingClientRect, 줌/팬 무관).
// 패딩 0인 노드는 skip. 실렌더(web)/PDF엔 없음(편집 보조 레이어).

import type React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import type { ComposerNode } from '@safetyhub/report-composer';
import { makeUniqKey, rectRelativeTo } from './canvasRect';

interface BandSpec {
  key: string;
  color: string;
  style: React.CSSProperties;
}

/**
 * 패딩 있는 모든 노드의 4변 패딩 띠를 별도 오버레이 레이어로 그린다(노드 CSS는 미접촉).
 * pointer-events:none이라 클릭/선택 방해 없음.
 *
 * ⚠️ 측정은 **커밋 후(useLayoutEffect)**, **deps 없이 매 커밋마다** 수행하고 결과 시그니처가 바뀔 때만
 * setState 한다. 이유:
 *  - render 도중 querySelectorAll/getBoundingClientRect로 재면, 표 구조(단 수/행) 변경 시 React가 새 표를
 *    커밋하기 *전*의 옛 DOM(이전 칼럼 좌표)을 측정해 이전 칸 경계의 띠가 남는 잔상이 생긴다.
 *  - useLayoutEffect라도 deps를 [body,zoom] 등으로 고정하면 CanvasViewport의 재측정 tick(forceTick,
 *    remeasureDep 기반 한 프레임 뒤 재렌더)에는 반응하지 못해 위치가 어긋난다.
 *  → deps를 비워 **매 커밋(재측정 tick 포함) 후 항상 최신 DOM을 재측정**하되, 시그니처 비교로 무한 리렌더를 막는다.
 * (2026-07-01 다단 표 세로선 잔상 = 패딩 오버레이 재측정 타이밍.)
 */
export function PaddingOverlay({
  canvas,
  body: _body,
}: {
  canvas: HTMLElement | null;
  /** template.body 참조 — 직접 읽지 않지만 EditorPage가 전달(재렌더 유발원 중 하나) */
  body: ComposerNode[];
}) {
  const sigRef = useRef<string>('');
  const [bands, setBands] = useState<BandSpec[]>([]);

  useLayoutEffect(() => {
    if (!canvas) {
      if (sigRef.current !== '') {
        sigRef.current = '';
        setBands([]);
      }
      return;
    }

    const NODE_PAD_COLOR = 'rgba(251, 146, 60, 0.22)'; // 노드 패딩(주황 반투명)
    const CELL_PAD_COLOR = 'rgba(59, 130, 246, 0.18)'; // 셀(td/th) 패딩(파랑 반투명)
    // 이보다 얇은 패딩은 띠를 그리지 않는다 — 1px대 패딩(컴팩트 표 셀 1px 4px 등)은 띠가
    // "셀 위 가로줄"로 보여 정보 없이 노이즈만 됨(2026-07-03 위험성 매트릭스 표 사용자 신고).
    const MIN_BAND = 3;
    const specs: BandSpec[] = [];
    const push = (key: string, color: string, style: React.CSSProperties) =>
      specs.push({ key, color, style });
    // 반복영역 전개 복제 셀/노드은 같은 정체성 id/좌표를 여러 DOM이 공유 → DOM 등장 순번 접미로 유일화.
    const uniq = makeUniqKey();

    // 노드 패딩 띠(주황) — DOM에 렌더된 노드 요소 직접 순회
    for (const el of canvas.querySelectorAll<HTMLElement>('[data-composer-node-id]')) {
      const cs = getComputedStyle(el);
      const pt = parseFloat(cs.paddingTop) || 0;
      const pr = parseFloat(cs.paddingRight) || 0;
      const pb = parseFloat(cs.paddingBottom) || 0;
      const pl = parseFloat(cs.paddingLeft) || 0;
      if (!pt && !pr && !pb && !pl) continue;

      const { top, left, width: w, height: h } = rectRelativeTo(canvas, el);
      const id = uniq(el.dataset.composerNodeId ?? '');

      if (pt >= MIN_BAND) push(`${id}-pt`, NODE_PAD_COLOR, { top, left, width: w, height: pt });
      if (pb >= MIN_BAND)
        push(`${id}-pb`, NODE_PAD_COLOR, { top: top + h - pb, left, width: w, height: pb });
      if (pl >= MIN_BAND)
        push(`${id}-pl`, NODE_PAD_COLOR, { top: top + pt, left, width: pl, height: h - pt - pb });
      if (pr >= MIN_BAND)
        push(`${id}-pr`, NODE_PAD_COLOR, {
          top: top + pt,
          left: left + w - pr,
          width: pr,
          height: h - pt - pb,
        });
    }

    // 표 셀(td/th) 패딩 띠(파랑) — DOM 직접 순회
    for (const cellEl of canvas.querySelectorAll<HTMLElement>('[data-composer-cell]')) {
      const cs = getComputedStyle(cellEl);
      const pt = parseFloat(cs.paddingTop) || 0;
      const pr = parseFloat(cs.paddingRight) || 0;
      const pb = parseFloat(cs.paddingBottom) || 0;
      const pl = parseFloat(cs.paddingLeft) || 0;
      if (!pt && !pr && !pb && !pl) continue;

      const { top, left, width: w, height: h } = rectRelativeTo(canvas, cellEl);
      const k = uniq(`cell-${cellEl.dataset.composerCell ?? ''}`);

      if (pt >= MIN_BAND) push(`${k}-pt`, CELL_PAD_COLOR, { top, left, width: w, height: pt });
      if (pb >= MIN_BAND)
        push(`${k}-pb`, CELL_PAD_COLOR, { top: top + h - pb, left, width: w, height: pb });
      if (pl >= MIN_BAND)
        push(`${k}-pl`, CELL_PAD_COLOR, { top: top + pt, left, width: pl, height: h - pt - pb });
      if (pr >= MIN_BAND)
        push(`${k}-pr`, CELL_PAD_COLOR, {
          top: top + pt,
          left: left + w - pr,
          width: pr,
          height: h - pt - pb,
        });
    }

    // 시그니처가 바뀔 때만 갱신(무한 리렌더 방지). DOM이 그대로면 skip.
    const sig = JSON.stringify(specs);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setBands(specs);
    }
  }); // deps 없음 — 매 커밋(재측정 tick 포함) 후 최신 DOM 측정

  if (bands.length === 0) return null;

  // 컨테이너 key = 띠 구조(각 셀/노드 key 조합, 위치 제외). 표 구조(단 수/행) 변경 시 key가 바뀌어
  // 컨테이너가 재마운트되며 옛 띠 DOM을 전부 제거한다(React 리스트 reconcile이 구조 변경 시 옛 자식을
  // 누적하던 문제 회피 — 2026-07-01 다단 세로선 잔상). 위치만 바뀌면(줌/스크롤) key 동일→자식 style만 갱신.
  const containerKey = bands.map((b) => b.key).join('|');

  return (
    <div
      key={containerKey}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 23 }}
      aria-hidden
    >
      {bands.map((b) => (
        <div key={b.key} style={{ position: 'absolute', background: b.color, ...b.style }} />
      ))}
    </div>
  );
}
